import React, { useContext } from 'react';
import {
  Col,
  Flex,
  Row,
  Tag,
  theme as antTheme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import {
  RenewalEvent,
  RevenueMix,
  TimelineSegment,
} from '@totem/components/accountPlan/types';
import {
  money,
  moneyExact,
  programColor,
  RENEWAL_COLORS,
  SectionHeading,
  shortDate,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import colors from '@totem/styles/colors';

const SEGMENT_STYLES: Record<TimelineSegment['kind'], React.CSSProperties> = {
  active: { background: colors.branding.primaryDark, color: '#ffffff' },
  optimize: { background: colors.branding.accent, color: '#ffffff' },
  pending: { background: '#3b82f6', color: '#ffffff' },
  renewal: { background: '#f59e0b', color: '#ffffff' },
  decision: { background: '#dc2626', color: '#ffffff' },
  tbd: {
    background:
      'repeating-linear-gradient(45deg, rgba(148,163,184,0.25) 0 6px, rgba(148,163,184,0.08) 6px 12px)',
    color: '#64748b',
  },
};

const SEGMENT_LEGEND: { kind: TimelineSegment['kind']; label: string }[] = [
  { kind: 'active', label: 'Active service term' },
  { kind: 'optimize', label: 'Optimize term' },
  { kind: 'pending', label: 'Pending contracted period' },
  { kind: 'renewal', label: 'Renewal — not yet negotiated' },
  { kind: 'decision', label: 'Decision required' },
  { kind: 'tbd', label: 'No contracted pricing' },
];

const Swatch = ({ style }: { style: React.CSSProperties }) => (
  <span
    style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: 2,
      marginRight: 4,
      ...style,
    }}
  />
);

const MixBars = ({ items }: { items: RevenueMix[] }) => {
  const { token } = antTheme.useToken();
  const max = Math.max(...items.map((item) => item.annual), 1);
  return (
    <>
      {items.map((item) => (
        <Flex
          key={item.name}
          align="center"
          gap={10}
          style={{ marginBottom: 6 }}
        >
          <Typography.Text
            style={{ width: 170, flexShrink: 0, fontSize: 12 }}
            ellipsis={{ tooltip: item.name }}
          >
            {item.name}
          </Typography.Text>
          <div
            style={{
              flex: 1,
              height: 10,
              background: token.colorFillSecondary,
              borderRadius: 5,
            }}
          >
            <div
              style={{
                width: `${(item.annual / max) * 100}%`,
                height: '100%',
                borderRadius: 5,
                background: programColor(item.name),
              }}
            />
          </div>
          <Typography.Text
            style={{ width: 90, textAlign: 'right', fontSize: 12 }}
          >
            {money(item.annual)} · {item.percent}%
          </Typography.Text>
        </Flex>
      ))}
    </>
  );
};

const RevenueViewCard = () => {
  const { data } = useContext(AccountPlanContext);
  const { token } = antTheme.useToken();
  if (!data) {
    return null;
  }
  const { revenue } = data.summary;
  const { timeline } = revenue;

  // ---- quarterly invoice starts
  const maxQuarter = Math.max(
    ...revenue.quarters.map((quarter) => quarter.total),
    1,
  );
  const quarterPrograms = Array.from(
    new Set(
      revenue.quarters.flatMap((quarter) =>
        quarter.programs.map((program) => program.name),
      ),
    ),
  ).sort();

  // ---- timeline geometry
  const windowStart = dayjs(timeline.start).valueOf();
  const windowSpan = dayjs(timeline.end).valueOf() - windowStart;
  const position = (value: string) =>
    ((dayjs(value).valueOf() - windowStart) / windowSpan) * 100;
  const years: number[] = [];
  for (
    let year = dayjs(timeline.start).year();
    year <= dayjs(timeline.end).year();
    year += 1
  ) {
    years.push(year);
  }
  const todayLeft = position(timeline.today);

  const renewalColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      width: 100,
      render: (date: string) => <b>{shortDate(date)}</b>,
    },
    {
      title: 'Agreement / lines',
      dataIndex: 'service',
      render: (service: string, event: RenewalEvent) => (
        <>
          {event.buildingName} — {service}
          {event.escalation1 > 0 && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Escalation sensitivity: +1% ≈ {moneyExact(event.escalation1)}/yr
                · +3% ≈ {moneyExact(event.escalation3)}/yr · +5% ≈{' '}
                {moneyExact(event.escalation5)}/yr
              </Typography.Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Annual value',
      dataIndex: 'annual',
      width: 120,
      align: 'right' as const,
      render: (annual: number) => moneyExact(annual),
    },
    {
      title: 'Action',
      dataIndex: 'actionLabel',
      width: 230,
      render: (label: string, event: RenewalEvent) => (
        <Tag color={RENEWAL_COLORS[event.action]}>{label}</Tag>
      ),
    },
  ];

  return (
    <PlanCard
      variant="live"
      title="Revenue View — IB account value & renewal risk"
      liveTip="Contracted invoice starts, revenue mix, term timeline and renewal events computed from sales order lines. Rollovers already signed are green; true renewal events are amber (negotiate) or red (decision)."
      extra={`~${money(revenue.annualRunRate)} annualized run rate · ${money(revenue.contractedBacklog)} contracted backlog`}
    >
      <Row gutter={32}>
        <Col xs={24} xl={12}>
          <SectionHeading inCard>
            Contracted invoice starts by quarter · by program
          </SectionHeading>
          {revenue.quarters.map((quarter) => (
            <Flex
              key={quarter.label}
              align="center"
              gap={10}
              style={{ marginBottom: 8 }}
            >
              <Typography.Text
                style={{ width: 52, flexShrink: 0, fontSize: 12 }}
              >
                {quarter.label}
                {quarter.future ? '*' : ''}
              </Typography.Text>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    height: 16,
                    width: `${(quarter.total / maxQuarter) * 85}%`,
                  }}
                >
                  {quarter.programs.map((program) => (
                    <Tooltip
                      key={program.name}
                      title={`${program.name}: ${moneyExact(program.annual)}`}
                    >
                      <div
                        style={{
                          width: `${program.percent}%`,
                          background: programColor(program.name),
                        }}
                      />
                    </Tooltip>
                  ))}
                </div>
                <Typography.Text
                  strong
                  style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  {money(quarter.total)}
                </Typography.Text>
              </div>
            </Flex>
          ))}
          <Flex
            wrap
            gap={12}
            style={{ fontSize: 11, color: token.colorTextSecondary }}
          >
            {quarterPrograms.map((name) => (
              <span key={name}>
                <Swatch style={{ background: programColor(name) }} />
                {name}
              </span>
            ))}
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            * quarter not yet complete — contracted, not yet invoiced in full.
          </Typography.Text>
        </Col>
        <Col xs={24} xl={12}>
          <SectionHeading inCard>
            Revenue mix · annualized run rate {money(revenue.annualRunRate)}
          </SectionHeading>
          <MixBars items={revenue.programs} />
          <SectionHeading>By building</SectionHeading>
          <MixBars
            items={revenue.buildingMix.filter((item) => item.annual > 0)}
          />
        </Col>
      </Row>

      <SectionHeading>
        Contract-term timeline · {dayjs(timeline.start).year()} –{' '}
        {dayjs(timeline.end).year()}
      </SectionHeading>
      <div
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: '8px 12px',
        }}
      >
        <div
          style={{
            display: 'flex',
            fontSize: 11,
            fontWeight: 600,
            color: token.colorTextSecondary,
          }}
        >
          <div style={{ width: 220, flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
            {years.map((year) => (
              <div
                key={year}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  borderLeft: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                {year}
              </div>
            ))}
            <div
              style={{
                position: 'absolute',
                left: `${todayLeft}%`,
                top: 0,
                color: token.colorError,
                fontSize: 10,
              }}
            >
              &nbsp;Today
            </div>
          </div>
        </div>
        {timeline.rows.map((row) => (
          <div
            key={row.label}
            style={{ display: 'flex', alignItems: 'center', marginTop: 6 }}
          >
            <Typography.Text
              style={{
                width: 220,
                flexShrink: 0,
                fontSize: 12,
                paddingRight: 8,
              }}
              ellipsis={{ tooltip: row.label }}
            >
              {row.label} ({money(row.annual)})
            </Typography.Text>
            <div style={{ flex: 1, position: 'relative', height: 20 }}>
              {row.segments.map((segment) => (
                <Tooltip
                  key={`${segment.kind}-${segment.start}`}
                  title={`${segment.label} (${shortDate(segment.start)} – ${shortDate(segment.end)})`}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: `${position(segment.start)}%`,
                      width: `${Math.max(position(segment.end) - position(segment.start), 0.5)}%`,
                      top: 0,
                      bottom: 0,
                      borderRadius: 3,
                      fontSize: 10,
                      lineHeight: '20px',
                      padding: '0 4px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      boxShadow: `inset -1px 0 0 ${token.colorBgContainer}`,
                      ...SEGMENT_STYLES[segment.kind],
                    }}
                  >
                    {segment.label}
                  </div>
                </Tooltip>
              ))}
              <div
                style={{
                  position: 'absolute',
                  left: `${todayLeft}%`,
                  top: -4,
                  bottom: -4,
                  width: 2,
                  background: token.colorError,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <Flex
        wrap
        gap={14}
        style={{ fontSize: 11, color: token.colorTextSecondary, marginTop: 6 }}
      >
        {SEGMENT_LEGEND.map((item) => (
          <span key={item.kind}>
            <Swatch style={SEGMENT_STYLES[item.kind]} />
            {item.label}
          </span>
        ))}
      </Flex>

      <SectionHeading>Upcoming renewals & term events</SectionHeading>
      <IBTable
        size="small"
        rowKey={(event: RenewalEvent) =>
          `${event.date}-${event.buildingName}-${event.action}`
        }
        columns={renewalColumns}
        dataSource={revenue.renewals}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          hideOnSinglePage: true,
        }}
        locale={{
          emptyText: 'No renewal or term events in the next 30 months',
        }}
      />
      <Typography.Paragraph
        type="secondary"
        style={{ marginTop: 10, marginBottom: 0, fontSize: 12 }}
      >
        <Typography.Text strong style={{ fontSize: 12 }}>
          Reconciliation:
        </Typography.Text>{' '}
        the Portal recognizes {moneyExact(revenue.recognizedActive)} across{' '}
        {revenue.activeLines} active lines; many lines are partial billing
        periods (quarterly or prorated), so annualizing each service gives ~
        {money(revenue.annualRunRate)} of run rate. The{' '}
        {money(revenue.contractedBacklog)} of pending lines is contracted
        future-period value on signed orders — backlog, not pipeline risk.
      </Typography.Paragraph>
    </PlanCard>
  );
};

export default RevenueViewCard;
